import { useState, useEffect } from "react";
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Switch, message,
  Tag, Popconfirm, Divider, Tooltip,
} from "antd";
import { Plus, Check, X, Pencil, Trash2, Package } from "lucide-react";
import { usePlanList, usePlanSave, usePlanDelete } from "../../../api-hooks/plans";
import { TPlan } from "../../../types";
import utils from "../../../util/utils";

const FEATURE_KEYS = [
  { key: "scheduling", label: "Scheduling" },
  { key: "invoicing", label: "Invoicing" },
  { key: "client_portal", label: "Client Portal" },
  { key: "lone_worker", label: "Lone Worker" },
  { key: "compliance_module", label: "Compliance" },
  { key: "custom_forms", label: "Custom Forms" },
  { key: "api_access", label: "API Access" },
  { key: "gps_tracking", label: "GPS Tracking" },
  { key: "advanced_reports", label: "Advanced Reports" },
  { key: "multi_branch", label: "Multi Branch" },
  { key: "sso", label: "SSO" },
  { key: "dedicated_support", label: "Dedicated Support" },
];

const isFeatureEnabled = (val: string | undefined) => val === "1" || val === "true";

export default function Plans() {
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<TPlan | null>(null);
  const [messageApi, ctx] = message.useMessage();
  const [form] = Form.useForm();

  const { data: plans = [], isLoading } = usePlanList();

  const { mutate: savePlan, isPending: saving } = usePlanSave({
    onSuccess: () => {
      messageApi.success(editingPlan ? "Plan updated." : "Plan created.");
      setShowModal(false);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to save plan."),
  });

  const { mutate: deletePlan } = usePlanDelete({
    onSuccess: () => messageApi.success("Plan deleted."),
    onError: () => messageApi.error("Cannot delete a plan with active companies."),
  });

  const openCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, is_public: true, currency: "GBP", sort_order: plans.length + 1 });
    setShowModal(true);
  };

  const openEdit = (plan: TPlan) => {
    setEditingPlan(plan);
    const featureFields: Record<string, boolean> = {};
    FEATURE_KEYS.forEach(({ key }) => {
      featureFields[`feat_${key}`] = isFeatureEnabled(plan.features?.[key]);
    });
    form.setFieldsValue({ ...plan, ...featureFields });
    setShowModal(true);
  };

  const handleSubmit = (vals: any) => {
    const features: Record<string, string> = {};
    FEATURE_KEYS.forEach(({ key }) => {
      features[key] = vals[`feat_${key}`] ? "1" : "0";
      delete vals[`feat_${key}`];
    });
    if (!vals.price_annual) vals.price_annual = 0;
    savePlan({ ...vals, features, id: editingPlan?.id });
  };

  const featureColumns = [
    {
      title: "Feature",
      dataIndex: "key",
      key: "key",
      width: 200,
      render: (_: string, row: { key: string; label: string }) => (
        <span className="text-sm text-slate-700">{row.label}</span>
      ),
    },
    ...plans.map((plan) => ({
      title: (
        <div className="text-center">
          <div className="font-semibold text-slate-800">{plan.name}</div>
          <div className="text-xs text-violet-600 font-medium">£{Number(plan.price_monthly).toFixed(0)}/mo</div>
        </div>
      ),
      key: `plan_${plan.id}`,
      align: "center" as const,
      render: (_: any, row: { key: string }) =>
        isFeatureEnabled(plan.features?.[row.key]) ? (
          <Check size={16} className="text-green-500 mx-auto" />
        ) : (
          <X size={14} className="text-slate-300 mx-auto" />
        ),
    })),
  ];

  const handleFormValuesChange = (changedValues: any) => {
    if (changedValues.name) {
      const generatedSlug = utils.removeSpecialChars(changedValues.name);
      form.setFieldsValue({ slug: generatedSlug });
    }
  };

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Plans</h2>
          <p className="text-slate-500 text-sm mt-0.5">Manage subscription plans and features</p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={openCreate}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          New Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className="rounded-2xl border-2 shadow-sm hover:shadow-md transition-all"
            style={{ borderColor: plan.is_active ? "#e9d5ff" : "#e2e8f0" }}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-bold text-slate-800 text-lg">{plan.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{plan.description || "—"}</div>
              </div>
              <Tag color={plan.is_active ? "green" : "default"}>{plan.is_active ? "Active" : "Inactive"}</Tag>
            </div>

            <div className="text-3xl font-bold text-violet-700 mb-1">
              £{Number(plan.price_monthly).toFixed(0)}
              <span className="text-base font-normal text-slate-400">/mo</span>
            </div>
            {Number(plan.price_annual) > 0 && (
              <div className="text-xs text-slate-400 mb-3">£{Number(plan.price_annual).toFixed(0)}/year</div>
            )}

            <div className="flex flex-wrap gap-1.5 mb-4 text-xs text-slate-500">
              <span className="bg-slate-100 px-2 py-0.5 rounded-full">
                {plan.max_users != null ? `${plan.max_users} users` : "Unlimited users"}
              </span>
              <span className="bg-slate-100 px-2 py-0.5 rounded-full">
                {plan.max_jobs_per_month != null ? `${plan.max_jobs_per_month} jobs/mo` : "Unlimited jobs"}
              </span>
              <span className="bg-slate-100 px-2 py-0.5 rounded-full">
                {plan.max_storage_gb != null ? `${plan.max_storage_gb}GB` : "Unlimited"} storage
              </span>
            </div>

            <div className="text-xs text-slate-400 mb-4">{plan.company_count ?? 0} companies on this plan</div>

            <div className="flex gap-2">
              <Button size="small" icon={<Pencil size={12} />} onClick={() => openEdit(plan)}>Edit</Button>
              <Tooltip title={(plan.company_count ?? 0) > 0 ? "Cannot delete — companies are on this plan" : ""}>
                <Popconfirm
                  title="Delete this plan?"
                  description="Companies on it will keep their current plan until changed."
                  onConfirm={() => deletePlan(plan.id)}
                  disabled={(plan.company_count ?? 0) > 0}
                >
                  <Button size="small" danger icon={<Trash2 size={12} />} disabled={(plan.company_count ?? 0) > 0}>
                    Delete
                  </Button>
                </Popconfirm>
              </Tooltip>
            </div>
          </Card>
        ))}

        {plans.length === 0 && !isLoading && (
          <div className="col-span-3 text-center py-16 text-slate-400">No plans yet. Create your first plan.</div>
        )}
      </div>

      {plans.length > 0 && (
        <Card
          title="Feature Matrix"
          className="rounded-2xl border border-slate-200 shadow-sm"
          styles={{ body: { padding: 0 } }}
        >
          <Table
            dataSource={FEATURE_KEYS}
            columns={featureColumns}
            rowKey="key"
            pagination={false}
            size="small"
            loading={isLoading}
          />
        </Card>
      )}

      <Modal
        open={showModal}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        footer={null}
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Package size={15} className="text-violet-600" />
            </div>
            <span>{editingPlan ? `Edit: ${editingPlan.name}` : "Create New Plan"}</span>
          </div>
        }
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleFormValuesChange} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="name"
              label="Plan Name"
              rules={[{ required: true, message: "Plan name is required" }, { min: 2, message: "Min 2 characters" }]}
              className="col-span-2"
            >
              <Input className="rounded-lg" placeholder="e.g. Professional" maxLength={100} />
            </Form.Item>

            <Form.Item
              name="slug"
              label="Slug (Unique Identifier)"
              rules={[{ required: true, message: "Slug is required" }, { min: 2, message: "Min 2 characters" }]}
              className="col-span-2"
            >
              <Input className="rounded-lg" placeholder="e.g. Slug" maxLength={100} />
            </Form.Item>

            <Form.Item name="description" label="Description" className="col-span-2">
              <Input.TextArea className="rounded-lg" rows={2} placeholder="Brief plan description" maxLength={500} showCount />
            </Form.Item>

            <Form.Item
              name="price_monthly"
              label="Monthly Price (£)"
              rules={[{ required: true, message: "Monthly price is required" }, { type: "number", min: 0, message: "Must be ≥ 0" }]}
            >
              <InputNumber min={0} precision={2} className="w-full rounded-lg" placeholder="49.00" prefix="£" />
            </Form.Item>

            <Form.Item
              name="price_annual"
              label="Annual Price (£)"
              rules={[{ type: "number", min: 0, message: "Must be ≥ 0" }]}
            >
              <InputNumber min={0} precision={2} className="w-full rounded-lg" placeholder="490.00" prefix="£" />
            </Form.Item>

            <Form.Item name="max_users" label="Max Users" extra="Leave blank for unlimited">
              <InputNumber min={1} className="w-full rounded-lg" placeholder="50" />
            </Form.Item>

            <Form.Item name="max_jobs_per_month" label="Max Jobs/Month" extra="Leave blank for unlimited">
              <InputNumber min={1} className="w-full rounded-lg" placeholder="1000" />
            </Form.Item>

            <Form.Item name="max_storage_gb" label="Storage (GB)" extra="Leave blank for unlimited">
              <InputNumber min={1} className="w-full rounded-lg" placeholder="25" />
            </Form.Item>

            <Form.Item name="sort_order" label="Sort Order" rules={[{ type: "number", min: 0 }]}>
              <InputNumber min={0} className="w-full rounded-lg" placeholder="1" />
            </Form.Item>
          </div>

          <div className="flex gap-8 py-2 px-3 bg-slate-50 rounded-xl mb-4">
            <Form.Item name="is_active" valuePropName="checked" label="Active" className="mb-0">
              <Switch />
            </Form.Item>
            <Form.Item name="is_public" valuePropName="checked" label="Publicly Visible" className="mb-0">
              <Switch />
            </Form.Item>
          </div>

          <Divider orientation="left" className="text-sm text-slate-500">Features</Divider>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 bg-slate-50 rounded-xl p-4">
            {FEATURE_KEYS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2.5">
                <Form.Item name={`feat_${key}`} valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
                <span className="text-sm text-slate-600">{label}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-slate-100">
            <Button onClick={() => { setShowModal(false); form.resetFields(); }}>Cancel</Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={saving}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

